import { Button } from "@appkit4/react-components/button";
import type { ComponentType } from "react";
import { Navigate } from "react-router-dom";

import { useDeal } from "../../../../api/deals";
import { getLoadErrorCopy } from "../../../../api/errors";
import { useCurrentUserProfile } from "../../../../api/userAccess";
import { AppRoutes } from "../../../../types/enums/appRoutes";
import type { IAppRoute } from "../../../../types/routesConfig";
import { hasAnyRole } from "../../../../utils/accessControl";
import { EmptyState } from "../../../shared";
import { Spinner } from "../../../shared/Spinner/Spinner";

function ProtectedRoute({
  path: _path,
  element: Component,
  roles,
  isMemo: _isMemo,
  requiresDeal = false,
  ...props
}: IAppRoute) {
  const { data: profile, isLoading, isError, error, refetch } = useCurrentUserProfile();
  const isRoleProtected = Boolean(roles && roles !== "*");
  const userRoleKeys = profile?.platformRoles?.map((r) => r.roleKey) ?? [];
  const isAuthorized = !isRoleProtected || hasAnyRole(userRoleKeys, roles);

  const {
    data: deal,
    isLoading: isDealLoading,
    isError: isDealError,
  } = useDeal({ enabled: requiresDeal && (!isRoleProtected || (!isLoading && !isError && isAuthorized)) });

  if (isRoleProtected) {
    if (isLoading) {
      return (
        <div style={{ display: "flex", justifyContent: "center", padding: "3rem" }}>
          <Spinner size="sm" />
        </div>
      );
    }

    if (isError) {
      const loadErrorCopy = getLoadErrorCopy(error, {
        title: "Could not verify your access.",
        description: "There was a problem loading your access profile. Please retry.",
        forbiddenTitle: "Could not verify your access (403 Forbidden).",
        forbiddenDescription:
          "Access denied (403). You do not have permission to view your access profile.",
      });

      return (
        <EmptyState
          title={loadErrorCopy.title}
          description={loadErrorCopy.description}
          action={
            <Button kind="secondary" type="button" onClick={() => void refetch()}>
              Retry
            </Button>
          }
        />
      );
    }

    if (!isAuthorized) {
      return <Navigate to={AppRoutes.forbidden} replace />;
    }
  }

  if (requiresDeal && isDealLoading) {
    return null;
  }

  if (requiresDeal && !isDealError && !deal) {
    return <Navigate to={AppRoutes.home} replace />;
  }

  const Comp = Component as ComponentType;
  return <Comp {...props} />;
}

export default ProtectedRoute;
